/* @flow */
import { DeliveryStatus } from 'chat/constants/DeliveryConstants';
import DeliveryModalCounters from 'chat/components/DeliveryModalCounters.jsx';
import DeliveryModalFocusedErrorView from 'chat/components/DeliveryModalFocusedErrorView.jsx';
import DeliveryModalList from 'chat/components/DeliveryModalList.jsx';
import DeliveryModalListItem from 'chat/components/DeliveryModalListItem.jsx';
import Dialog from 'components/Dialog.jsx';
import DialogBody from 'components/DialogBody.jsx';
import Row from 'components/Row.jsx';
import Avatar from 'components/Avatar.jsx';
import Link from 'components/Link.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import MessageDeliverySummary from 'entities/MessageDeliverySummary'; // eslint-disable-line
import type {MessageDeliverySummaryUserType} from 'entities/MessageDeliverySummary'; // eslint-disable-line
import React from 'react';
import ReadReceipt from 'chat/components/ReadReceipt.jsx';
import ScrollableArea from 'components/ScrollableArea.jsx';
import Text from 'components/Text.jsx';
import TitleHeader from 'components/TitleHeader.jsx';

import './styles/DeliveryModal.scss';

type State = {
  /* errored user are clickable, when clicked we show a focused view */
  focusedUser: ?MessageDeliverySummaryUserType,
};

type Props = {
  /* The message delivery summary this can either be a broadcast or a chat message summary */
  summary: ?MessageDeliverySummary,
  /* if the summary has been loaded (summary can still be null) */
  loaded: boolean,
  /* if the summary has been loaded but is still null, tooSoon would
   * indicated that it might become available in the near future */
  tooSoon: boolean,
};

class DeliveryModal extends React.Component {
  props: Props;

  state: State = {
    focusedUser: null,
  };

  getAvatar(text: string, colorHex: ?string) {
    return <Avatar size="small" text={text} colorHex={colorHex || undefined} />;
  }

  getReadReceiptIcon(status: any) {
    return <ReadReceipt iconSize="xxxsmall" labelDisplay glyphDisplay status={status} />;
  }

  setFocusedUser: Function = (focusedUser: MessageDeliverySummaryUserType): void => {
    this.setState({ focusedUser });
  };

  clearFocusedUser: Function = (): void => {
    this.setState({ focusedUser: null });
  };

  renderDeliverySuccess() {
    const { summary } = this.props;

    if (!summary) {
      return null;
    }

    if (summary.getSuccessUsers().length === 0) {
      const success = summary.getSuccessCount();

      return (
        <DeliveryModalList title="Delivered">
          <li>
            <Text color="thunder" size="xxsmall">
              This data isn’t currently available.
              <br />
              {success === 1 && `The message was delivered to ${success} participant.`}
              {success > 1 && `The message was delivered to ${success} participants.`}
            </Text>
          </li>
        </DeliveryModalList>
      );
    }

    const renderedSuccessDeliveries = summary.getSuccessUsers().map(user => {
      return (
        <DeliveryModalListItem
          avatar={user.initials && this.getAvatar(user.initials, user.color)}
          key={user.uuid}
          text={user.name}
          info={this.getReadReceiptIcon(DeliveryStatus.DELIVERED)}
        />
      );
    });

    return (
      <DeliveryModalList title="Delivered">
        {renderedSuccessDeliveries}
      </DeliveryModalList>
    );
  }

  renderRead() {
    const { summary } = this.props;

    if (!summary) {
      return null;
    }

    const renderedRead = summary.getReadUsers().map((user: MessageDeliverySummaryUserType) => {
      return (
        <DeliveryModalListItem
          avatar={user.initials && this.getAvatar(user.initials, user.color)}
          key={user.uuid}
          text={user.name}
          info={this.getReadReceiptIcon(DeliveryStatus.READ)}
        />
      );
    });

    return (
      <DeliveryModalList
        title="Read"
        subtext={
          `Read receipts are available for messages viewed on the app and attachments viewed via
          text or email.`
        }>
        {renderedRead}
      </DeliveryModalList>
    );
  }

  renderDeliveryError() {
    const { summary } = this.props;

    if (!summary) {
      return null;
    }

    const renderedErroredDeliveries = summary.getFailedUsers().map(user => {
      return (
        <Link
          key={user.uuid}
          color="inherit"
          onClick={() => this.setFocusedUser(user)}>
          <DeliveryModalListItem
            avatar={user.initials && this.getAvatar(user.initials, user.color)}
            info={this.getReadReceiptIcon(DeliveryStatus.DELIVERY_ERROR)}
            text={user.name}
          />
        </Link>
      );
    });

    return (
      <DeliveryModalList title="Delivery errors">
        <Row>
          {renderedErroredDeliveries}
        </Row>
      </DeliveryModalList>
    );
  }

  renderNotAvailable() {
    let title = 'Delivery summary not available';
    let subtext;

    if (this.props.tooSoon) {
      title = 'Check back later';
      subtext = 'Your delivery summary might take a few seconds to load. It should be ready soon!';
    }

    return (
      <TitleHeader
        image={require('assets/images/lighthouse.png')}
        imageClassName="DeliveryModal-unavailableImage"
        title={title}
        subtext={subtext}
        bottomMargin="none"
        size="small"
      />
    );
  }

  renderContent() {
    const { summary } = this.props;

    if (!summary) {
      if (this.props.loaded) {
        return this.renderNotAvailable();
      } else {
        return <LoadingIndicator height={400} />;
      }
    }

    return (
      <div>
        <Row>
          <DeliveryModalCounters summary={summary} />
        </Row>
        <ScrollableArea className="DeliveryModal-details">
          {
            summary.getFailedCount() > 0 &&
              this.renderDeliveryError()
          }
          {
            summary.getMessageType() === 'broadcast' &&
              <div>
                <Row>
                  {this.renderRead()}
                </Row>
                {this.renderDeliverySuccess()}
              </div>
          }
        </ScrollableArea>
      </div>
    );
  }

  render() {
    if (this.state.focusedUser) {
      return (
        <DeliveryModalFocusedErrorView
          {...this.props}
          name={this.state.focusedUser.name}
          onClose={this.clearFocusedUser}
        />
      );
    }

    return (
      <Dialog
        eventScreenName="DeliveryModal"
        {...this.props}
        className="DeliveryModal"
        size="small"
        title="Delivery summary">
        <DialogBody>
          {this.renderContent()}
        </DialogBody>
      </Dialog>
    );
  }
}

export default DeliveryModal;



// WEBPACK FOOTER //
// ./src/chat/components/DeliveryModal.jsx